import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';

const MobileSearchModal = lazy(() => import('./MobileSearchModal.jsx'));

const topNavItems = [
  { label: 'Home', path: '/', icon: 'home' },
  { label: 'Predictor', path: '/predictor', icon: 'predictor' },
  { label: 'Seat Matrix & Cutoff', path: '/cutoff-trends', icon: 'matrix' },
  { label: 'Colleges', path: '/colleges', icon: 'college' },
];

const bottomNavItems = [
  { label: 'Cutoff Trends', path: '/cutoff-trends-comparison', icon: 'trend-chart' },
  { label: 'Admission Guide', path: '/admission-guide', icon: 'guide' },
  { label: 'Resources', path: '/resources', icon: 'resources' },
  { label: 'About', path: '/about', icon: 'about' },
];

const mobileCoreTools = [
  { label: 'Home', path: '/', icon: 'home', desc: 'Admissions overview' },
  { label: 'College Predictor', path: '/predictor', icon: 'predictor', desc: 'Predict admission chances' },
  { label: 'Seat Matrix & Cutoff', path: '/cutoff-trends', icon: 'matrix', desc: 'Official BCECEB cutoffs' },
  { label: 'Explore Colleges', path: '/colleges', icon: 'college', desc: '300+ Bihar institutions' },
  { label: 'Cutoff Trends', path: '/cutoff-trends-comparison', icon: 'trend-chart', desc: 'Multi-year round comparisons' },
  { label: 'Admission Guide', path: '/admission-guide', icon: 'guide', desc: 'Step-by-step counselling' },
  { label: 'Study Resources', path: '/resources', icon: 'resources', desc: 'BEU syllabus & PYQs' },
];

const mobilePlatformItems = [
  { label: 'About CampusGuide', path: '/about', icon: 'about' },
  { label: 'Terms & Conditions', path: '/terms-and-conditions', icon: 'terms' },
  { label: 'Privacy Policy', path: '/privacy-policy', icon: 'privacy' },
];

const Icon = ({ type, className = 'h-5 w-5' }) => {
  switch (type) {
    case 'home':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5 10.5V20h14v-9.5" />
          <path d="M9 20v-6h6v6" />
        </svg>
      );
    case 'predictor':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20v-11" />
        </svg>
      );
    case 'college':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m3 10 9-5 9 5" />
          <path d="M5 10v9" />
          <path d="M19 10v9" />
          <path d="M3 19h18" />
          <path d="M9 14h6" />
        </svg>
      );
    case 'matrix':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M3 9h18" />
          <path d="M3 15h18" />
          <path d="M9 3v18" />
          <path d="M15 3v18" />
        </svg>
      );
    case 'trend':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 18V6" />
          <path d="M10 18v-8" />
          <path d="M16 18v-4" />
          <path d="M22 18V3" />
          <path d="M3 18h19" />
        </svg>
      );
    case 'trend-chart':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
      );
    case 'guide':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="6" y="3" width="12" height="18" rx="2" />
          <path d="M9 7h6" />
          <path d="M9 11h6" />
          <path d="M9 15h4" />
        </svg>
      );
    case 'resources':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 6h16" />
          <path d="M4 12h16" />
          <path d="M4 18h10" />
        </svg>
      );
    case 'about':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" />
          <path d="M4 20a8 8 0 0 1 16 0" />
        </svg>
      );
    case 'terms':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      );
    case 'privacy':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      );
    case 'menu':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case 'close':
      return (
        <svg viewBox="0 0 24 24" width="20" height="20" className={`${className} shrink-0`} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 6l12 12M18 6 6 18" />
        </svg>
      );
    default:
      return null;
  }
};

const Navbar = ({
  currentPath,
  isDark,
  onNavigate,
  onInstall,
  isInstalled,
  isMobileMenuOpen: externalMenuOpen,
  setIsMobileMenuOpen: setExternalMenuOpen,
}) => {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const isMobileMenuOpen = externalMenuOpen !== undefined ? externalMenuOpen : internalMenuOpen;
  const setIsMobileMenuOpen = setExternalMenuOpen || setInternalMenuOpen;
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Lock background scroll when drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      // Focus close button on open
      window.setTimeout(() => closeButtonRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setIsCompact((current) => {
        if (current) {
          return window.scrollY > 16;
        }
        return window.scrollY > 48;
      });
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const isActivePath = (path) =>
    currentPath === path ||
    (path === '/colleges' && currentPath.startsWith('/colleges/')) ||
    (path === '/resources' && currentPath.startsWith('/resources/')) ||
    (path === '/cutoff-trends-comparison' &&
      (currentPath === '/cutoff-trends-comparison' ||
        currentPath === '/engineering-cutoff-trends' ||
        currentPath === '/cutoff-comparison' ||
        currentPath === '/all-cutoff-trends'));

  const navLinkClass = (isActive) =>
    `inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-3.5 text-sm font-bold transition duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/15 ${
      isActive
        ? 'bg-blue-50 text-blue-700 shadow-[0_12px_24px_-18px_rgba(37,99,235,0.7)] dark:border dark:border-[#214b93] dark:bg-[#0f2f63] dark:text-white'
        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950 dark:text-white dark:hover:bg-[#0d1f40]'
    }`;

  const drawerItemClass = (isActive) =>
    `flex items-center gap-3.5 rounded-xl px-3 py-2.5 text-left transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
      isActive
        ? 'bg-blue-50 font-bold text-blue-700 dark:bg-blue-950/70 dark:text-blue-300'
        : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-950 dark:text-slate-200 dark:hover:bg-slate-800/60 dark:hover:text-white'
    }`;

  const handleMenuNavigate = (e, path) => {
    if (e && (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey)) {
      return; // allow opening in new tab
    }
    if (e) e.preventDefault();
    onNavigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-slate-50/92 backdrop-blur-xl dark:border-slate-900 dark:bg-[#030b1c]/95">
      <nav className={`mx-auto w-full max-w-[116rem] px-4 transition-all duration-200 sm:px-6 lg:px-10 ${isCompact ? 'py-2' : 'py-3'}`}>
        <div className={`rounded-[20px] border border-slate-200/90 bg-white/95 px-3 shadow-[0_16px_44px_-28px_rgba(15,23,42,0.42)] transition-all duration-200 dark:border-[#1e355f] dark:bg-[#06142b] dark:shadow-[0_18px_50px_-30px_rgba(2,8,23,0.7)] sm:px-4 ${isCompact ? 'py-2' : 'py-2.5'}`}>
          {/* Mobile Header Bar */}
          <div className="flex items-center justify-between gap-2 lg:hidden">
            <a
              href="#/"
              onClick={(e) => handleMenuNavigate(e, '/')}
              className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
              aria-label="CampusGuide by Ashwini Home"
            >
              <span className="h-9 w-9 shrink-0 sm:h-10 sm:w-10">
                <img
                  src="/campusguide-logo.webp"
                  alt="CampusGuide logo"
                  width="40"
                  height="44"
                  decoding="async"
                  className="h-full w-full object-contain"
                />
              </span>
              <span className="block h-7 max-w-[10.5rem] sm:h-8 sm:max-w-[12rem]">
                <img
                  src="/campusguide-wordmark-clean.webp"
                  alt="CampusGuide by Ashwini"
                  width="180"
                  height="32"
                  decoding="async"
                  className="h-full w-full object-contain object-left"
                />
              </span>
            </a>

            {/* Mobile Header Actions (Search only) */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setIsMobileSearchOpen(true)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 shadow-xs transition hover:bg-slate-100 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-[#29466f] dark:bg-[#081a35] dark:text-white dark:hover:text-blue-400"
                aria-label="Open Search"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
              </button>
            </div>
          </div>

          {/* Desktop Navigation Layout */}
          <div className="hidden lg:flex lg:items-center lg:justify-between lg:gap-5">
            <div className="flex items-center gap-3">
              <a
                href="#/"
                onClick={(e) => handleMenuNavigate(e, '/')}
                className="flex min-w-0 shrink-0 items-center gap-3 text-left"
              >
                <span
                  className={`shrink-0 transition-all duration-200 ${
                    isCompact ? 'h-12 w-12' : 'h-14 w-14 xl:h-16 xl:w-16'
                  }`}
                >
                  <img
                    src="/campusguide-logo.webp"
                    alt="CampusGuide by Ashwini logo"
                    width="64"
                    height="64"
                    decoding="async"
                    className="h-full w-full object-contain"
                  />
                </span>
                <div className={`hidden w-px bg-slate-200 transition-all duration-200 dark:bg-[#29466f] sm:block ${isCompact ? 'h-9' : 'h-11 xl:h-12'}`} />
                <span
                  className={`block transition-all duration-200 ${
                    isCompact ? 'h-9 w-[12.5rem]' : 'h-10 w-[14rem] xl:h-11 xl:w-[15.5rem]'
                  }`}
                >
                  <img
                    src="/campusguide-wordmark-clean.webp"
                    alt="CampusGuide by Ashwini"
                    width="240"
                    height="40"
                    decoding="async"
                    className="h-full w-full object-contain object-left"
                  />
                </span>
              </a>


            </div>

            <div className="ml-auto flex w-full max-w-[50rem] flex-col gap-2 transition-all duration-200 xl:max-w-[56rem]">
              <div className="grid grid-cols-4 w-full gap-2">
                {topNavItems.map((item) => (
                  <a
                    key={item.path}
                    href={`#${item.path}`}
                    onClick={(e) => handleMenuNavigate(e, item.path)}
                    className={`${navLinkClass(isActivePath(item.path))} w-full`}
                  >
                    <Icon type={item.icon} className="h-5 w-5 shrink-0" />
                    <span>{item.label}</span>
                  </a>
                ))}
              </div>

              <div className="grid grid-cols-4 w-full gap-2">
                {bottomNavItems.map((item) => (
                  <a
                    key={item.path}
                    href={`#${item.path}`}
                    onClick={(e) => handleMenuNavigate(e, item.path)}
                    className={`${navLinkClass(isActivePath(item.path))} w-full`}
                  >
                    <Icon type={item.icon} className="h-5 w-5 shrink-0" />
                    <span>{item.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Accessible Mobile Drawer & Backdrop */}
      {isMobileMenuOpen && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-xs transition-opacity lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />

          <div
            id="mobile-navigation-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation Menu"
            className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-[310px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-[#1e355f] dark:bg-[#06142b] lg:hidden"
          >
        {/* Drawer Header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-slate-100 px-4 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <img
              src="/campusguide-logo.webp"
              alt="CampusGuide logo"
              width="32"
              height="32"
              className="h-8 w-8 object-contain"
            />
            <span className="text-sm font-black tracking-tight text-slate-900 dark:text-white">
              CampusGuide
            </span>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={() => setIsMobileMenuOpen(false)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            aria-label="Close menu"
          >
            <Icon type="close" className="h-5 w-5 shrink-0" />
          </button>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-3.5 py-4">


          {/* Section 1: Main Tools */}
          <div className="space-y-1">
            <p className="px-2 text-[10.5px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Decision Tools
            </p>
            {mobileCoreTools.map((item) => {
              const active = isActivePath(item.path);
              return (
                <a
                  key={item.path}
                  href={`#${item.path}`}
                  onClick={(e) => handleMenuNavigate(e, item.path)}
                  className={drawerItemClass(active)}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${
                      active
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    <Icon type={item.icon} className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold leading-tight">{item.label}</p>
                    <p className="text-[10.5px] text-slate-400 dark:text-slate-500 truncate">
                      {item.desc}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="my-3.5 border-t border-slate-100 dark:border-slate-800" />

          {/* Section 2: Platform & Legal */}
          <div className="space-y-1">
            <p className="px-2 text-[10.5px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Platform &amp; Legal
            </p>
            {mobilePlatformItems.map((item) => {
              const active = isActivePath(item.path);
              return (
                <a
                  key={item.path}
                  href={`#${item.path}`}
                  onClick={(e) => handleMenuNavigate(e, item.path)}
                  className={drawerItemClass(active)}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${
                      active
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    <Icon type={item.icon} className="h-4 w-4" />
                  </span>
                  <span className="text-xs font-semibold">{item.label}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Drawer Footer Note */}
        <div className="border-t border-slate-100 bg-slate-50/70 px-4 py-3 text-center dark:border-slate-800 dark:bg-slate-900/40">
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            CampusGuide by Ashwini &bull; Bihar Admissions
          </p>
        </div>
      </div>
        </>,
        document.body
      )}

      {isMobileSearchOpen && typeof document !== 'undefined' && createPortal(
        <Suspense fallback={null}>
          <MobileSearchModal
            isOpen={isMobileSearchOpen}
            onClose={() => setIsMobileSearchOpen(false)}
            onNavigate={onNavigate}
          />
        </Suspense>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
